import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SessionExpiredModal from '../components/auth/SessionExpiredModal';
import {
  registerSessionExpiredHandler,
  SessionEndReason,
  setSessionExpiredModalOpen,
  suppressSessionExpiredModal,
  notifySessionExpired,
} from '../lib/sessionExpired';
import { clearSession, ensureSession } from '../lib/swotifyAuth';

const INITIAL = {
  open: false,
  reason: SessionEndReason.EXPIRED,
  message: '',
};

function isAuthPublicPath(pathname = '') {
  return (
    pathname === '/login' ||
    pathname.startsWith('/login') ||
    pathname === '/terms' ||
    pathname === '/privacy' ||
    pathname === '/help'
  );
}

export default function SessionExpiredProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [modal, setModal] = useState(INITIAL);

  useEffect(() => {
    const unregister = registerSessionExpiredHandler((payload) => {
      const next = {
        open: true,
        reason: payload.reason || SessionEndReason.EXPIRED,
        message:
          payload.message ||
          'Your session has expired for security. Please sign in again to continue.',
      };
      // Idempotent on purpose. A dead session makes every in-flight request
      // fail, so this handler fires repeatedly; returning the SAME state object
      // lets React bail out instead of re-rendering per failed request, which
      // otherwise loops (render → refetch → 401 → notify → render...).
      setModal((prev) =>
        prev.open && prev.reason === next.reason && prev.message === next.message
          ? prev
          : next,
      );
    });

    return () => {
      unregister();
      setSessionExpiredModalOpen(false);
    };
  }, []);

  // PWA / day-to-day restore. After 30 days idle, force login.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const hadSession = Boolean(
        localStorage.getItem('swotify_access_token') || localStorage.getItem('swotify_role'),
      );
      let token = null;
      try {
        token = await ensureSession();
      } catch {
        token = null;
      }
      if (cancelled) return;
      if (token || isAuthPublicPath(location.pathname)) return;

      // No usable session on a protected route: the user must not be left here.
      //
      // This used to also require `hadSession`, which made it unreliable —
      // `ensureSession()` clears the session itself when the refresh fails, and
      // App.jsx calls it too. If that call won the race, `hadSession` was
      // already false by the time this ran, so nothing fired. The user stayed
      // on the dashboard with no token, and because the fetch interceptor skips
      // requests with no auth context, every following 401 was ignored: pages
      // kept loading empty and no sign-in was ever demanded.
      if (hadSession) {
        notifySessionExpired({
          reason: SessionEndReason.EXPIRED,
          message:
            'Your session expired after 30 days of inactivity. Please sign in again to continue.',
        });
      } else {
        // Never had a session in this tab (or it was already cleared elsewhere)
        // — just send them to sign in rather than claiming it "expired".
        clearSession();
        navigate('/login', { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigate]);

  const handleLoginAgain = useCallback(() => {
    suppressSessionExpiredModal();
    setSessionExpiredModalOpen(false);
    clearSession();
    setModal(INITIAL);
    navigate('/login', { replace: true });
  }, [navigate]);

  return (
    <>
      {children}
      <SessionExpiredModal
        open={modal.open}
        reason={modal.reason}
        message={modal.message}
        onLoginAgain={handleLoginAgain}
      />
    </>
  );
}
